<template>
  <busy-area :busy="loadingHistoryDifference">
    <h2>{{ fullTitle }}</h2>
    <div class="flex flex-wrap gap-x-10 gap-y-10 pb-10 pt-5">
      <div class="flex flex-col justify-stretch">
        <table class="statisticsTable h-full">
          <thead>
          <tr>
            <th>{{ $t('pages.history.property') }}</th>
            <th>{{ $t('pages.history.value') }}</th>
            <th>{{ $t('pages.history.property') }}</th>
            <th>{{ $t('pages.history.value') }}</th>
            <th>{{ $t('pages.history.property') }}</th>
            <th>{{ $t('pages.history.value') }}</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>{{ $t('pages.history.countPastCampaigns') }}</td>
            <td>{{ historyDifferenceItem?.countPastCampaigns }}</td>
            <td>{{ $t('pages.history.countPotentialCampaignSlots') }}</td>
            <td>{{ historyDifferenceItem?.countPotentialCampaignSlots }}</td>
            <td>{{ $t('pages.history.countUsedCampaignSlots') }}</td>
            <td>{{ historyDifferenceItem?.countUsedCampaignSlots }}</td>
          </tr>
          <tr>
            <td>{{ $t('pages.history.countActiveCampaigns') }}</td>
            <td>{{ historyDifferenceItem?.countActiveCampaigns }}</td>
            <td>{{ $t('pages.history.countActiveLicenses') }}</td>
            <td>{{ historyDifferenceItem?.countActiveLicenses }}</td>
            <td>{{ $t('pages.history.countPastLicenses') }}</td>
            <td>{{ historyDifferenceItem?.countPastLicenses }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </busy-area>
</template>

<script lang="ts" setup>
import BusyArea from '@utils/busy/BusyArea.vue';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useProvider } from '@utils/table/hooks/provider';
import type { DriverHistoryType, Region } from '@/entities/Entities';
import {computed} from "vue";

export interface HistoryGeneralDifferenceDto {
  startDate?: Date;
  endDate?: Date;
  region: Region | null;
  driverType: DriverHistoryType;
  countPastCampaigns: number;
  countPotentialCampaignSlots: number;
  countUsedCampaignSlots: number;
  countActiveCampaigns: number;
  countActiveLicenses: number;
  countPastLicenses: number;
}

interface Props {
  providerParams: object;
}

const { t } = useI18n();

const props = defineProps<Props>();

const {
  data: historyDifferenceItem,
  loading: loadingHistoryDifference,
} = useProvider<HistoryGeneralDifferenceDto>('/history_generals/difference', props.providerParams);

function formatDate(date: Date | undefined) {
  return date && format(new Date(date), 'dd.MM.yyyy - HH:mm:ss');
}

const regionTitle = computed(() => {
  const regionName = historyDifferenceItem.value?.region?.name;
  if (regionName) {
    return `${t('pages.history.difference.title.forRegion')} ${regionName}`;
  }
  return `${t('pages.history.difference.title.forAllRegions')}`;
});

const fullTitle = computed(() => {
  const startDate = formatDate(historyDifferenceItem.value?.startDate);
  const endDate = formatDate(historyDifferenceItem.value?.endDate);
  return `${t('pages.history.difference.title.differenceFrom')} ${startDate} ${t('pages.history.difference.title.to')} ${endDate} ${regionTitle.value}`;
});

</script>

<style lang="scss" scoped>
.statisticsTable {
  @apply border;

  tr {
    td, th {
      &:not(:last-child) {
        @apply border-r;
      }

      @apply px-5 py-2;
      text-align: right;
    }

    th {
      @apply border-b;
    }
  }
}
</style>
